import { useEffect, useState } from 'react';
import { BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  getPushStatus,
  isPushSupported,
  registerPush,
  unsubscribePush,
  type PushStatus,
} from '@/lib/pushNotifications';

/**
 * N7 (86d3w2kf4) IN-1 — the web-push control, as a labelled switch in the notification popover
 * footer. Replaces the sidebar-footer `NotificationBell` (CSC-295), which this ticket deletes.
 *
 * <b>Why it moved.</b> That bell rendered a bell glyph beside a real Notifications nav item, so the
 * shell carried two bells with opposite meanings — one opened an inbox, the other silently toggled
 * browser push. A switch with a visible label cannot be mistaken for an inbox, and sitting in the
 * popover footer puts it where someone is already thinking about notifications.
 *
 * <b>⚠️ NOT permission-gated — owner ruling 2026-08-25</b> ("it doesn't make sense for it to be
 * gated"). The bell and the Profile card both gate on `quotations.review_inbox`; this does not, and
 * the only gate left is browser support. One consequence a future reader should know rather than
 * rediscover: today the ONLY sender of a web push in the whole system is
 * `EmailIngestionService.cs` → `SendToPermissionAsync("quotations.review_inbox", …)` for AutoDraft
 * quotations. So for a user outside that permission the switch subscribes the browser correctly and
 * simply has nothing to deliver yet. That is understood and accepted — the notification centre is
 * expected to grow its own sender, and this control is then already in place for everyone.
 *
 * All four states of the CSC-295 machinery survive verbatim, because `src/lib/pushNotifications.ts`
 * is reused rather than reimplemented: on · off · denied (disabled, with the recovery tooltip) ·
 * unsupported (disabled, explains why).
 */
export function DesktopPushToggle() {
  const [status, setStatus] = useState<PushStatus>('off');

  /**
   * The user's INTENT, held only while the request is in flight.
   *
   * The switch renders `pending ?? (status === 'on')`, so the thumb moves on the click rather than
   * after a permission prompt and a round trip — the ~100ms acknowledgement the responsiveness gate
   * requires. It is cleared in `finally`, at which point the real `status` takes over, so a denied
   * prompt corrects itself instead of leaving a switch that lies.
   */
  const [pending, setPending] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getPushStatus().then((next) => {
      if (!cancelled) setStatus(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Rendered rather than hidden: someone whose browser cannot do push should be able to see WHY
  // they get no desktop alerts, without hunting through docs for the answer.
  if (!isPushSupported() || status === 'unsupported') {
    return (
      <ToggleShell
        label="Desktop notifications"
        hint="Not supported in this browser"
        tooltip="This browser doesn't support desktop notifications"
      >
        <BellOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      </ToggleShell>
    );
  }

  const isBlocked = status === 'denied';
  const checked = pending ?? status === 'on';
  const tooltip = isBlocked
    ? 'Blocked by the browser — re-enable notifications for this site in your browser settings'
    : checked
      ? 'Desktop notifications are on — click to turn off'
      : 'Desktop notifications are off — click to turn on';

  async function handleChange(next: boolean) {
    // Permission was refused at the browser level, so there is nothing this switch can do about it.
    // Say so instead of firing a request that cannot succeed.
    if (isBlocked) {
      toast.error('Notifications are blocked. Re-enable them in your browser site settings.');
      return;
    }

    setPending(next);
    try {
      // The click IS the user gesture browsers require for the permission prompt, so `registerPush`
      // has to be reached from the handler itself — not from an effect reacting to state.
      const resolved = next ? await registerPush() : await unsubscribePush();
      setStatus(resolved);

      if (resolved === 'on') toast.success('Desktop notifications enabled');
      else if (resolved === 'denied') {
        toast.error('Notifications were blocked. Re-enable them in your browser site settings.');
      } else if (!next) toast.success('Desktop notifications disabled');
    } catch (err) {
      console.error('Push toggle failed:', err);
      toast.error('Could not update notifications');
    } finally {
      setPending(null);
    }
  }

  return (
    <ToggleShell
      label="Desktop notifications"
      hint={isBlocked ? 'Blocked in browser settings' : undefined}
      tooltip={tooltip}
    >
      <Switch
        checked={checked}
        onCheckedChange={(next) => void handleChange(next)}
        disabled={pending !== null}
        aria-label={tooltip}
        className={cn(isBlocked && 'opacity-50')}
      />
    </ToggleShell>
  );
}

/**
 * The shared row: visible text label on the left, control on the right, one tooltip over the whole
 * thing. The label is what keeps this out of the icon-only-control rule — but the tooltip and the
 * matching `aria-label` on the `Switch` stay regardless, since the label names the setting while the
 * tooltip names what a click will DO.
 */
function ToggleShell({
  label,
  hint,
  tooltip,
  children,
}: {
  label: string;
  hint?: string;
  tooltip: string;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex min-w-0 items-center gap-2">
          <div className="min-w-0">
            <p className="truncate text-xs font-medium text-foreground">{label}</p>
            {hint && <p className="truncate text-xs text-muted-foreground">{hint}</p>}
          </div>
          {children}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltip}</TooltipContent>
    </Tooltip>
  );
}
